<!-- LeaderboardModal.vue -->
<template>
  <div class="modal-container">
    <img
      src="images/close.svg"
      class="close"
      @click="resetModal"
      role="button"
      aria-label="Close"
      alt="X to close modal"
      title="Close"
    />
    <div class="modal-content">
      <div>
        <transition-group :name="titleFade">
          <h2 :key="title">{{ title }}</h2>
        </transition-group>
      </div>
      <div class="modal-main">
        <knot-carousel
          :use-slide-names="true"
          :slide-class="slideClass"
          @page-changed="pageChange"
        >
          <template v-slot:About>
            <img
              src="images/grl-shield.svg"
              id="grl-shield"
              title="Golden Ratio League"
              alt="Shield for Golden Ratio League"
            />
            <p class="centered-text large-text">
              Welcome to the <strong class="grl">Golden Ratio League</strong>,
              where every player counts.
            </p>
            <p class="centered-text large-text">
              Solve today's Knotilus puzzle and push your team higher on the
              leaderboard.
            </p>
            <p class="centered-text large-text">
              Save your progress to be placed on a team that changes every
              season. If you don't, your results still contribute to the
              <strong>Galaxy</strong>, a collective of unknown players.
            </p>
            <p class="centered-text large-text">
              You can check the current standings on the next page.
            </p>
            <p
              class="centered-text large-text"
              v-if="livePlaysLeftAlert == null"
            >
              <strong> Play today and see how far your team can climb. </strong>
            </p>
            <p class="centered-text large-text" v-else>
              <strong>
                {{ livePlaysLeftAlert }} in the Golden Ratio League!</strong
              >
            </p>
            <p class="centered-text">
              Huge thanks to <strong>Anton Firsik</strong> for designing the
              Golden Ratio League icons! Explore more of their portfolio on
              <a
                target="_blank"
                href="https://www.fiverr.com/firsik?utm_source=knotilus"
                >Fiverr</a
              >.
            </p>
          </template>
          <template v-slot:Weekly>
            <template v-if="!allWeekLeaderboardsLoaded">
              <div class="spinner">
                <img src="images/spiral.svg" alt="spiral" title="Loading" />
                <div class="large">Loading Weekly Leaderboard Data</div>
              </div>
            </template>
            <template v-else-if="!noWeekData">
              <div class="centered-objects">
                <knot-select
                  :items="leaderboardWeeks"
                  @select-item="knotSelect"
                  :defaultItem="selectedWeek"
                  :horizontal="false"
                  :itemWidth="22"
                  :itemHeight="3"
                  id="selectedWeek"
                  :activeSelect="activeSelect"
                ></knot-select>
              </div>
              <h2 class="centered-text">
                {{ weekLeaderboardDateLabel }}
              </h2>
              <div class="centered-objects">
                <knot-select
                  :items="metrics"
                  @select-item="knotSelect"
                  :defaultItem="selectedMetric"
                  :horizontal="false"
                  :itemWidth="22"
                  :itemHeight="3"
                  id="selectedMetric"
                  :activeSelect="activeSelect"
                ></knot-select>
              </div>
                <div class="leaderboard-description-container">
              <transition
                name="metric-description"
              >
                <div
                  :key="selectedMetricDescription.key"
                  class="leaderboard-description"
                  v-html="selectedMetricDescription.desc"
                ></div>
              </transition>
                </div>
              <transition-group class="leaderboard" tag="div" name="metric-row">
                <div
                  v-for="record in selectedMetricLeaderboard"
                  :key="record.team_id"
                  class="leaderboard-row"
                  :class="{
                    selected:
                      record.team_id === selectedWeekTeam.id ||
                      (selectedWeekTeam.id === undefined &&
                        teamsData[record.team_id].is_default),
                  }"
                >
                  <div class="leaderboard-rank">
                    {{ leaderboardRank(record.rank) }}
                  </div>
                  <img
                    class="leaderboard-image"
                    :src="teamsData[record.team_id].image_url"
                  />
                  <div class="leaderboard-title">
                    {{ teamsData[record.team_id].title }}
                  </div>
                  <div class="leaderboard-metric">
                    {{ displayMetric(record.value, selectedMetric) }}
                  </div>
                </div>
              </transition-group>
              <p
                class="centered-text large-text"
                v-if="livePlaysLeftAlert != null"
              >
                <strong>
                  {{ livePlaysLeftAlert }} in the Golden Ratio League!</strong
                >
              </p>
            </template>
            <template v-else>
              <div class="important-note centered-text">Nothing here yet!</div>
              <div class="important-note centered-text">Check back later</div>
            </template>
          </template>
          <template v-slot:Season>
            <template v-if="!allSeasonLeaderboardsLoaded">
              <div class="spinner">
                <img src="images/spiral.svg" alt="spiral" title="Loading" />
                <div class="large">Loading Season Medal Data</div>
              </div>
            </template>
            <template v-else-if="!noSeasonData">
              <div class="centered-objects">
                <knot-select
                  :items="leaderboardSeasons"
                  @select-item="knotSelect"
                  :defaultItem="selectedSeason"
                  :horizontal="false"
                  :itemWidth="22"
                  :itemHeight="3"
                  id="selectedSeason"
                  :activeSelect="activeSelect"
                ></knot-select>
              </div>
              <h2 class="centered-text">
                {{ seasonLeaderboardDateLabel }}
              </h2>
              <div class="leaderboard-description-container">
                <div class="medals-description">
                  Each week, teams earn a rank in each of the seven category
                  leaderboards from the previous page. Season Medals count how
                  often a team finishes in each rank across all weeks of the
                  season.
                </div>
              </div>
              <div class="medals-header">
                <div></div>
                <div></div>
                <div class="medals-title">1<sup>st</sup></div>
                <div class="medals-title">2<sup>nd</sup></div>
                <div class="medals-title">3<sup>rd</sup></div>
                <div class="medals-title">Total</div>
              </div>
              <transition-group class="leaderboard" tag="div" name="metric-row">
                <div
                  v-for="record in selectedSeasonMedalsLeaderboard"
                  :key="record.team_id"
                  class="medals-row"
                  :class="{
                    selected:
                      record.team_id === selectedSeasonTeam.id ||
                      (selectedSeasonTeam.id === undefined &&
                        teamsData[record.team_id].is_default),
                  }"
                >
                  <img
                    class="leaderboard-image"
                    :src="teamsData[record.team_id].image_url"
                  />
                  <div class="leaderboard-title">
                    {{ teamsData[record.team_id].title }}
                  </div>
                  <div class="leaderboard-metric">
                    {{ record.medals["1"] }}
                  </div>
                  <div class="leaderboard-metric">
                    {{ record.medals["2"] }}
                  </div>
                  <div class="leaderboard-metric">
                    {{ record.medals["3"] }}
                  </div>
                  <div class="leaderboard-metric">
                    {{ medalTotal(record.medals) }}
                  </div>
                </div>
              </transition-group>
              <p
                class="centered-text large-text"
                v-if="livePlaysLeftAlert != null"
              >
                <strong>
                  {{ livePlaysLeftAlert }} in the Golden Ratio League!</strong
                >
              </p>
            </template>
            <template v-else>
              <div class="important-note centered-text">Nothing here yet!</div>
              <div class="important-note centered-text">Check back later</div>
            </template>
          </template>
        </knot-carousel>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "LeaderboardModal",
  props: {
    resetModal: {
      type: Function,
      required: true,
    },
    leaderboardData: {
      type: Object,
      required: true,
    },
    team: {
      type: Object,
      required: true,
    },
    elapsedTimeFormater: {
      type: Function,
      required: true,
    },
    priorSeasonTeam: {
      type: Object,
      required: true,
    },
    teamsData: {
      type: Object,
      required: true,
    },
    livePlaysLeftAlert: {
      type: [null,String],
      required: true,
    },
  },
  data() {
    return {
      title: "",
      titleFade: "",
      slideClass: { All: "", Overall: "" },
      selectedPeriodType: null,
      selectedWeek: null,
      selectedMetric: null,
      selectedSeason: null,
      metricDescriptions: {
        Errors:
          "The average number of mistakes team members make before finding the correct solution. Fewer errors mean sharper accuracy in spotting the right sequences.",
        "Hints Used":
          "The average number of hints team members rely on while solving. Some prefer figuring things out alone, while others like a little extra help along the way.",
        "Play Time":
          "The typical time it takes team members to finish a puzzle from start to finish. Quick times show speedy recognition.",
        Submissions:
          "Tracks the average number of submissions per puzzle. Teams with lower numbers tend to spot the right sequences more quickly and decisively.",
        "Live Puzzles Completed":
          "The average number of puzzles completed on release day. Highlighting how many team members are jumping in each day.",
        "Sequences Found":
          "Reflects how many sequences team members uncover on average. Those venturing into higher levels tend to find more.",
        "Ultimate Challenge":
          "The percentage of team members tackling Level 12 in HARD mode, exploring the toughest challenge.",
      },
      metricDefaults: {
        Errors: "NA",
        "Hints Used": "NA",
        "Play Time": "NA",
        Submissions: "NA",
        "Live Puzzles Completed": "0",
        // percent_sequences_found: "NA",
        "Sequences Found": "NA",
        "Ultimate Challenge": "NA",
      },
      selection: {},
      options: {},
      activeSelect: "",
      slideTitles: {
        About: "What is the Golden Ratio League?",
        Weekly: "Golden Ratio League Weekly Standings",
        Season: "Golden Ratio League Season Medals",
      },
    };
  },
  computed: {
    weekLeaderboardDateLabel() {
      if (this.allWeekLeaderboardsLoaded) {
        return this.leaderboardData.week[this.selectedWeek].date_label;
      }
      return "";
    },
    seasonLeaderboardDateLabel() {
      if (this.allSeasonLeaderboardsLoaded) {
        return this.leaderboardData.season[this.selectedSeason].date_label;
      }
      return "";
    },
    selectedSeasonTeam() {
      return this.leaderboardData.season[this.selectedSeason].current === true
        ? this.team
        : this.priorSeasonTeam;
    },
    selectedWeekTeam() {
      return this.leaderboardData.week[this.selectedWeek].current_season ===
        true
        ? this.team
        : this.priorSeasonTeam;
    },
    noTeamData() {
      return Object.keys(this.teamsData ?? {}).length == 0;
    },
    noWeekData() {
      const weekProxy = this.leaderboardData?.week ?? {}
      return (
        this.noTeamData ||
        !("week" in this.leaderboardData ?? {}) ||
        ("message" in weekProxy && Object.keys(weekProxy).length === 1)
      );
    },
    noSeasonData() {
      return (
        this.noTeamData ||
        !("season" in this.leaderboardData ?? {}) ||
        "message" in (this.leaderboardData?.season ?? {})
      );
    },
    allWeekLeaderboardsLoaded() {
      return Object.keys(this.leaderboardData.week || {}).length >0;
    },
    allSeasonLeaderboardsLoaded() {
      return Object.keys(this.leaderboardData.season || {}).length > 0 ;
    },
    leaderboardWeeks() {
      if (!this.allWeekLeaderboardsLoaded) {
        return [];
      }
      const keys = Object.keys(this.leaderboardData.week).sort((k1, k2) =>
        this.leaderboardData.week[k2].sort_by.localeCompare(
          this.leaderboardData.week[k1].sort_by
        )
      );
      return keys;
    },
    leaderboardSeasons() {
      if (!this.allSeasonLeaderboardsLoaded) {
        return [];
      }
      const keys = Object.keys(this.leaderboardData.season).sort((k1, k2) =>
        this.leaderboardData.season[k2].sort_by.localeCompare(
          this.leaderboardData.season[k1].sort_by
        )
      );
      return keys;
    },
    metrics() {
      return Object.keys(
        this.leaderboardData?.week?.[this.selectedWeek]?.data || {}
      );
    },
    selectedMetricDescription() {
      const key = this.selectedMetric ?? "init_key";
      const desc = this.metricDescriptions[key] || "";

      return { key, desc };
    },
    selectedMetricLeaderboard() {
      if (!this.allWeekLeaderboardsLoaded) {
        return {};
      }
      const leaderboard =
        this.leaderboardData.week[this.selectedWeek].data[this.selectedMetric];
      leaderboard.sort((a, b) => {
        if (a.rank === null) return 1;
        if (b.rank === null) return -1;
        return a.rank - b.rank;
      });
      return leaderboard;
    },
    selectedSeasonMedalsLeaderboard() {
      if (!this.allSeasonLeaderboardsLoaded) {
        return {};
      }
      const leaderboard = this.leaderboardData.season[this.selectedSeason].data;
      leaderboard.sort((a, b) => {
        return (
          b["medals"]["1"] - a["medals"]["1"] ||
          b["medals"]["2"] - a["medals"]["2"] ||
          b["medals"]["3"] - a["medals"]["3"]
        );
      });
      return leaderboard;
    },
  },
  created() {},
  watch: {
    metrics: {
      handler: function (newValue) {
        this.selectedMetric ||= newValue[0];
      },
      deep: true,
      immediate: true,
    },
    leaderboardWeeks: {
      handler: function (newValue) {
        this.selectedWeek = newValue[0];
      },
      deep: true,
      immediate: true,
    },
    leaderboardSeasons: {
      handler: function (newValue) {
        this.selectedSeason = newValue[0];
      },
      deep: true,
      immediate: true,
    },
  },
  mounted() {},
  methods: {
    leaderboardRank(rank) {
      if (rank == null) {
        return "-";
      } else {
        return rank;
      }
    },
    medalTotal(medals) {
      return Object.values(medals).reduce((acc, curr) => acc + curr);
    },
    pageChange(newPageIndex, oldPageIndex, behavior, newPageName) {
      this.title = this.slideTitles[newPageName];
      if (behavior === "smooth") {
        this.titleFade =
          oldPageIndex < newPageIndex ? "move-right-fade" : "move-left-fade";
      } else {
        this.titleFade = null;
      }
    },
    displayMetric(value, metric) {
      if (value == null) {
        return this.metricDefaults[metric];
      }
      if (metric === "Ultimate Challenge") {
        return `${value}%`;
      }
      if (metric === "Play Time") {
        return this.elapsedTimeFormater(value);
      }
      return value;
    },
    knotSelect(selection, id, active) {
      if (active) {
        this.activeSelect = id;
      } else {
        this.activeSelect = "";
      }
      this[id] = selection;
    },
    weekSelect(week, id, active) {
      if (active) {
        this.activeSelect = id;
      } else {
        this.activeSelect = "";
      }
      this.selectedWeek = week;
    },
    metricSelect(metric, id, active) {
      if (active) {
        this.activeSelect = id;
      } else {
        this.activeSelect = "";
      }
      this.selectedMetric = metric;
    },
  },
};
</script>

<style scoped>
.leaderboard-description-container {
  position: relative;
  height: 4.5em;
}
.leaderboard {
  display: flex;
  gap: 1vh;
  flex-direction: column;
  
}
.medals-header {
  font-size: 1.75vh;
  grid-template-columns: 4vh 3fr repeat(4, 1fr);
  padding: 1vh 2vh 0;
  height: 3vh;
}
.medals-row {
  font-size: 2.25vh;
  grid-template-columns: 4vh 3fr repeat(4, 1fr);
  height: 6.5vh;
}
.leaderboard-row,
.medals-row {
  padding: 1vh 2vh;
}
.leaderboard-row {
  font-size: 3vh;
  grid-template-columns: 3vh 5vh auto auto;
  height: 7vh;
}
.leaderboard-row,
.medals-row,
.medals-header {
  display: grid;
  gap: 1vh;
  font-weight: bold;
  border-radius: 0.5vh;
  z-index: 1;
  border: 1px solid rgba(0, 0, 0, 0);
  transition: border 0.5s ease;
}
.leaderboard-row.selected,
.medals-row.selected {
  border: 1px solid var(--border-color);
  z-index: 99;
  background-color: var(--modal-background-color);
}

.leaderboard-row div,
.medals-row div,
.medals-header div {
  align-items: center;
  display: flex;
  color: var(--border-color);
}
.leaderboard-row .leaderboard-image {
  width: 5vh;
}
.medals-row .leaderboard-image {
  width: 4vh;
}
.leaderboard-image {
  border-radius: 50%;
}
.leaderboard-rank,
.leaderboard-metric {
  font-size: 3vh;
}
.leaderboard-metric,
.medals-title {
  justify-content: center;
}
.medals-title sup {
  color: inherit;
  padding-bottom: 0.675em;
  font-size: 0.675em;
}
.leaderboard-metric:last-child,
.medals-title:last-child {
  justify-content: flex-end;
}
.leaderboard-title {
  padding-left: 2vh;
}
.leaderboard-description, .medals-description  {
  text-align: center;
  margin: 0 3vh;
  align-items: center;
  display: flex;
  position: absolute;
  height: 100%;
}
.centered-text {
  white-space: pre-wrap;
}
.speed-benchmark {
  display: flex;
  align-items: center;
}
.speed-benchmark .label {
  border-right: 5px solid var(--border-color);
  text-align: center;
}
.single-axis-benchmark {
  display: flex;
  gap: 0.5vh;
  padding: 0.5vh;
  border-radius: 0.5vh;
}
.single-axis-benchmark.selected {
  border: 1px solid #000;
}

.single-axis-benchmark.selected .bar {
  background: var(--border-color);
}
.single-axis-benchmark .header {
  flex: 0 0 auto;
  text-align: right;
}
.single-axis-benchmark .bar {
  flex: 0 0 auto;
  border-radius: 5px;
  background-color: var(--disabled);
}
.single-axis-benchmark .label {
  flex: 1 0 auto;
  text-align: left;
}
#overall-benchmarks {
  text-align: center;
  vertical-align: middle;
  padding: 0;
  margin: 0;
  width: 100%;
  border-collapse: collapse;
}
.overall-level-label {
  text-align: right;
  width: fit-content;
}
.overall-highlight {
  background: var(--border-color);
  color: var(--modal-background-color) !important;
}
#overall-benchmarks td,
#overall-benchmarks th {
  padding: 0;
  margin: 0;
}

#overall-benchmarks td {
  width: calc((100% - 1%) / 2);
}
#overall-benchmarks tr:not(:last-child) td,
#overall-benchmarks tr:not(:last-child) th {
  border-bottom: 1px solid var(--primary-color);
}

#overall-benchmarks td:not(:last-child),
#overall-benchmarks th:not(:last-child):not(:first-child) {
  border-right: 1px solid var(--primary-color);
}
#overall-benchmarks tbody th:first-child {
  width: 1%; /* Ensures the first header column is as small as possible */
  white-space: nowrap; /* Prevents wrapping in the first column */
}

#overall-benchmarks td div,
.speed-benchmark .label {
  font-size: 3.5vh;
  font-weight: bold;
  color: var(--border-color);
  font-family: "Fira Sans", system-ui;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0.5vh 1vh;
  padding: 1vh;
}
#overall-benchmarks td div {
  border-radius: 10px;
}
#overall-benchmarks th {
  font-size: 1.75vh;
  white-space: pre-line;
  font-weight: normal;
}
.stats-flex {
  gap: 0.875vh;
  display: flex;
  flex-direction: column;
}
#container {
  justify-content: center;
}
.stats-box {
  display: flex;
  gap: 25px;
  justify-content: center;
  align-items: center;
  margin: 0 20px;
  height: 10vh;
}
.stats-cell {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  gap: 2px;
  overflow: visible;
}

.stats-chart {
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.stats-chart-row {
  display: flex;
  gap: 5px;
}

.stats-chart-row-label {
  text-align: right;
  font-weight: bold;
  width: 7vh;
}

.stats-chart-bar {
  background: #666;
  padding: 0 1vh;
  position: relative;
}

.bar-highlight {
  background: var(--border-color);
  padding: 0 1vh;
  position: relative;
}

.stats-chart-bar:before {
  content: attr(data-label);
  position: absolute;
  right: 0; /* Align label to the right */
  padding: 0 5px; /* Padding for label */
  color: var(--modal-background-color); /* Text color */
  font-weight: bold;
  white-space: nowrap; /* Prevent text wrap */
}

.stats-img {
  height: 8vh;
}
button.stats-btn {
  border: 0;
  font-size: 2vh;
  display: flex;
  gap: 10px;
}
.share {
  background: var(--share-color);
  color: var(--modal-background-color);
}
.archive {
  background: var(--border-color);
  color: var(--modal-background-color);
}
button img {
  height: 2.5vh;
}
.privacy-note {
  font-size: 1.25em;
  margin: 0 8.5vmin;
}
.knotilus-logo img {
  height: 1em;
}

.lvl-stats-container {
  position: relative;
}

.puzzle-time {
  font-weight: bold;
  font-size: 3vh;
  font-family: "Fira Sans", system-ui;
}
#leaderboard {
  border-collapse: collapse;
}
#leaderboard th,
#leaderboard td {
  text-align: center;
  border-bottom: 1px solid var(--primary-color);
  padding: 1vh 0;
}
h2 {
  text-transform: capitalize;
}

.large-text {
  font-size: 1.25em;
  margin: 1.5vh 0;
}
.grl {
  color: #f9a93e;
  font-family: "Sanchez", system-ui;
}
#grl-shield {
  height: 10vh;
  display: flex;
  justify-self: center;
}
.metric-row-move {
  transition: transform 0.5s ease;
}
</style>
